// css imports
import './assets/css/index.scss';
import './assets/css/responsive.scss';
import './assets/css/print.scss';
import "./ui/custom/react-credit-cards/styles.scss";
import 'tui-image-editor/dist/tui-image-editor.min.css';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import 'react-lazy-load-image-component/src/effects/opacity.css';
import "react-toastify/dist/ReactToastify.css";
import 'react-device-frameset/styles/marvel-devices.min.css'
import 'owl.carousel/dist/assets/owl.carousel.css';
import 'owl.carousel/dist/assets/owl.theme.default.css';
import 'bootstrap/scss/bootstrap-grid.scss';
import 'bootstrap/scss/bootstrap.scss';
import 'jquery/dist/jquery.slim';

// Chart.js imports
import {TreemapController, TreemapElement} from "chartjs-chart-treemap";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {FunnelController, TrapezoidElement} from 'chartjs-chart-funnel';
import {HierarchicalScale} from 'chartjs-plugin-hierarchical';
import {BoxAndWiskers, BoxPlotController, Violin, ViolinController} from '@sgratzl/chartjs-chart-boxplot'
import {
    BubbleMapController,
    ChoroplethController,
    ColorScale,
    GeoFeature,
    ProjectionScale,
    SizeScale
} from 'chartjs-chart-geo';
import {MatrixController, MatrixElement} from 'chartjs-chart-matrix';
import {
    LinearAxis,
    LineSegment,
    LogarithmicAxis,
    LogarithmicParallelCoordinatesController,
    ParallelCoordinatesController,
    PCPScale
} from 'chartjs-chart-pcp';
import {Flow, SankeyController} from 'chartjs-chart-sankey';
import {ArcSlice, VennDiagramController} from 'chartjs-chart-venn';

// Application imports
import React from 'react';
import ReactDOM from 'react-dom/client';
import Application from './ui';
import reportWebVitals from './report-web-vitals';
import CrispService from "./core/services/crisp";
import DateUtils from './core/services/date-utils';
import YupExtensions from "./ui/components/form/yup-extensions";
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip
} from "chart.js";
import Sortable, {MultiDrag} from 'sortablejs';
import EnvService from "./core/services/env";
import Clarity from "@microsoft/clarity";
import {ApiExecutor, ApiExecutorEffect} from './@bizkeytech/base/core';
import UIUtils from './ui/utils';
import BizProductionApiService from "./core/services/api_service/production_api_service";
import {initializeInternationalization} from "./i18n";
import i18next from "i18next";
import AuthBlockerService from "./core/services/auth-blocker";
import {installDisableNumberInputWheel} from "./ui/utils/disable-number-input-wheel";

export const RootElement = document.getElementById('root')!;
export const ReactRoot = ReactDOM.createRoot(RootElement);

/**
 * The driver of the application.
 */
class Driver {

    /**
     * Runs the application.
     */
    public static run(): void {
        this.initializeAuthBlocker();
        this.initializeInternationalization().then();
        this.initializeScrollbar().then();
        this.initializeAPIServices();
        this.mountSortablePlugins();
        this.registerYarnExtensions();
        this.registerChartJS();
        this.registerDateUtils();
        this.registerCrispService();
        installDisableNumberInputWheel();
        this.render();
        this.reportWebVitals();
        this.registerClarity();
    }

    /**
     * Renders the application.
     * @private
     */
    private static render(): void {
        // STRICT MODE WILL INTENTIONALLY CALL USE_REDUCER AND USE_STATE's INITIAL METHODS MULTIPLE TIMES TO TEST THE PURIDITY OF SUCH
        //  FUNCTIONS. IT ALSO CHECKS FOR THE PROBLEMS IN CONCURRENT MODE SO THE PERFORMANCE IS INTENTIONALLY WORSE. PLEASE BE AWARE!!!
        // IF A DISPATCH OF A USE REDUCER THROWS, THE DISPATCHER AUTOMATICALLY RECALLS THE DISPATCH IN HOPE THAT THE ERROR IS RESOLVED
        ReactRoot.render(
            // <StrictMode>
            <Application/>
            // </StrictMode>
        );
    }

    /**
     * Initializes the auth blocker to prevent multiple tabs from sending requests when the user is not authenticated.
     * @private
     */
    private static initializeAuthBlocker(): void {
        AuthBlockerService.attachSyncListener();
    }


    /**
     * Initializes the internationalization.
     * @private
     */
    private static async initializeInternationalization() {
        await initializeInternationalization();
        if (EnvService.DEV_multiLanguageSupport) {
            this.applyGoogleTranslateFixForMultiLanguage();
        } else {
            // we have to revert the language back to default - English
            void i18next.changeLanguage(EnvService.defaultLocale);
        }
    }

    /**
     * Initializes whether we need to force the webkit scrollbar or not.
     * @private
     */
    private static async initializeScrollbar() {
        await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve));
        // Exclude Edge which also includes 'Chrome' in UA
        const isChrome: boolean = /Chrome/.test(navigator.userAgent) && !/Edg/.test(navigator.userAgent);
        const chromeVersionArray: RegExpExecArray | null = /Chrome\/([0-9]+)/.exec(navigator.userAgent);
        const chromeVersion: number = chromeVersionArray ? parseInt(chromeVersionArray[1], 10) : 0;
        const isModernChrome: boolean = isChrome && chromeVersion >= 121;

        document.body.classList.toggle('force-webkit-scrollbar', isModernChrome);
    }

    /**
     * Registers the chart js components.
     * @private
     */
    private static registerChartJS(): void {
        Chart.register(
            LineElement,
            BarElement,
            ArcElement,
            PointElement,
            Title,
            Tooltip,
            Legend,
            ChartDataLabels,
            CategoryScale,
            LinearScale,
            HierarchicalScale,
            FunnelController,
            TrapezoidElement,
            TreemapElement,
            TreemapController,
            BoxPlotController,
            BoxAndWiskers,
            ViolinController,
            Violin,
            ChoroplethController,
            BubbleMapController,
            GeoFeature,
            ColorScale,
            ProjectionScale,
            SizeScale,
            MatrixController,
            MatrixElement,
            LineSegment,
            LinearAxis,
            LogarithmicAxis,
            PCPScale,
            ParallelCoordinatesController,
            LogarithmicParallelCoordinatesController,
            SankeyController,
            Flow,
            VennDiagramController,
            ArcSlice,
        );
    }

    /**
     * Registers the date utils.
     * @private
     */
    private static registerDateUtils(): void {
        DateUtils.run();
    }

    /**
     * Registers the crisp service.
     * @private
     */
    private static registerCrispService(): void {
        CrispService.register();
    }

    /**
     * Applies a fix for the Google Translate plugin when using multiple languages.
     *
     * Original GitHub Issue: https://github.com/facebook/react/issues/11538#issuecomment-417504600
     * Chromium Issue to track: https://issues.chromium.org/issues/41407169
     * @private
     */
    private static applyGoogleTranslateFixForMultiLanguage() {
        if (typeof window !== 'undefined' && !window.__googleTranslateFixApplied) {

            const originalRemoveChild = Node.prototype.removeChild;
            Node.prototype.removeChild = function <T extends Node>(child: T): T {
                try {
                    return originalRemoveChild.call(this, child) as T;
                } catch (error: unknown) {
                    if ((error as any).name === 'NotFoundError') {
                        // Prevents crashes by ignoring the error
                        return child;
                    }
                    throw error;
                }
            };

            const originalInsertBefore = Node.prototype.insertBefore;
            Node.prototype.insertBefore = function <T extends Node>(newNode: T, referenceNode: Node | null): T {
                try {
                    return originalInsertBefore.call(this, newNode, referenceNode) as T;
                } catch (error: unknown) {
                    if ((error as any).name === 'NotFoundError') {
                        // If insertion fails, append the element instead
                        return this.appendChild(newNode);
                    }
                    throw error;
                }
            };

            window.__googleTranslateFixApplied = true;
        }
    }

    /**
     * Registers the Clarity service.
     * @private
     */
    private static registerClarity(): void {
        if (!EnvService.isProduction || !EnvService.inMainEnvironment) {
            (window as any).clarity?.('stop');
        } else {
            Clarity.init(EnvService.clarityProjectId);
        }
    }

    /**
     * Reports the web vitals.
     * @private
     */
    private static reportWebVitals(): void {
        reportWebVitals();
    }

    /**
     * Initializes the YARN extensions.
     * @private
     */
    private static registerYarnExtensions() {
        YupExtensions.injectExtensions();
    }

    /**
     * Mounts the sortable plugins.
     * @private
     */
    private static mountSortablePlugins() {
        Sortable.mount(new MultiDrag());
    }

    /**
     * Initializes the api services and inject the effects that are used by the ApiService.
     */
    private static initializeAPIServices(): void {
        BizProductionApiService.executor = new ApiExecutor();
        const showToastsEffect: ApiExecutorEffect = {
            id: 'showToastApiCallEffect',
            effect: UIUtils.showToastApiCallEffect.bind(UIUtils)
        }
        const captureScreenshotEffect: ApiExecutorEffect = {
            id: 'captureScreenshotApiCallEffect',
            effect: UIUtils.captureScreenshotApiCallEffect.bind(UIUtils)
        }
        BizProductionApiService.injectEffects([captureScreenshotEffect, showToastsEffect]);
    }
}

Driver.run();
